import React, { useContext, useMemo } from "react";
import {
  AutocompleteDropdown,
  FabekeDropdown,
  SimpleDropdown,
} from "@/components";
import { useTextDomainContext } from "@/app/providers";
import { useRolesOnlyList } from "@/hooks";

interface Role {
  id: string;
  role: string;
  key: string;
  createdAt: string;
  updatedAt: string;
  value: string;
  label: string;
}

interface RolesDropdownCommonProps {
  componentLabel?: string;
  defaultValue?: string;
  type?: string;
  onChange?: (_event: any) => void;
  required?: boolean;
  visualStyle: "autoComplete" | "autoCompleteMulti" | "simple" | "old";
}

/**
 * Roles dropdown component
 */
export const RolesDropdownCommon = (
  props: RolesDropdownCommonProps,
): React.ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { rolesList } = useRolesOnlyList();

  // Calculate options using useMemo to avoid state management
  const options = useMemo(() => {
    let returnValue: Role[] = [];
    if (!props.type) {
      // returnValue stays as []
    } else if (props.type === "super") {
      returnValue = rolesList;
    } else {
      returnValue = rolesList.filter((option) =>
        option.key.startsWith(props.type + "-"),
      );
    }
    return returnValue;
  }, [props.type, rolesList]);

  //#region useMemo
  const dropdownProps = useMemo(() => {
    const ddProps: any = {
      options,
      componentLabel: gettext("Roles"),
      defaultValue: "",
      visualStyle: "autoComplete",
      size: "small",
    };

    if (props.componentLabel) {
      ddProps.componentLabel = props.componentLabel;
    }

    if (props.defaultValue) {
      ddProps.defaultValue = props.defaultValue;
    }

    if (props.required) {
      ddProps.required = props.required;
    }

    return ddProps;
  }, [
    gettext,
    options,
    props.componentLabel,
    props.defaultValue,
    props.required,
  ]);
  //#endregion useMemo

  //#region Handlers
  /**
   * Handles change event of the roles dropdown
   */
  const handleChange = (_event: any) => {
    if (typeof props.onChange === "function") {
      props.onChange(_event);
    }
  };
  //#endregion Handlers

  return (
    <>
      {props.visualStyle === "autoComplete" && (
        <AutocompleteDropdown onChange={handleChange} {...dropdownProps} />
      )}

      {props.visualStyle === "simple" && (
        <SimpleDropdown onChange={handleChange} {...dropdownProps} />
      )}

      {props.visualStyle === "old" && (
        <FabekeDropdown onChange={handleChange} {...dropdownProps} />
      )}
    </>
  );
};
